import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import {
  Badge, EmptyState, PageLoading, SearchInput, Select,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import CustomerDetail from './CustomerDetail';
import CustomerCreateDialog from './CustomerCreateDialog';
import CustomerImport from './CustomerImport';
import { exportXlsx } from '../reports/reportExport';
import type { Area, Customer, CustomerList, CustomerListColumn, ReportColumn } from '../../types';

const STATUS_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'danger'> = {
  active: 'success',
  pending_approval: 'warning',
  inactive: 'neutral',
  rejected: 'danger',
};

function cellValue(customer: Customer, column: CustomerListColumn): string {
  if (column.key === 'name') return customer.name;
  if (column.key === 'route_id') return customer.route?.name ?? '—';
  return customer.fields[column.key]?.display || '—';
}

export default function CustomersPage() {
  const toast = useToast();
  const { can } = useAuth();
  const canManage = can('manager', 'company_admin', 'platform_admin');

  const [rows, setRows] = useState<Customer[]>([]);
  const [columns, setColumns] = useState<CustomerListColumn[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [areas, setAreas] = useState<Area[]>([]);
  const [search, setSearch] = useState('');
  const [routeId, setRouteId] = useState('');
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Typing in the search box should not fire a request per keystroke.
  const debounced = useRef<number | null>(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (debounced.current) window.clearTimeout(debounced.current);
    debounced.current = window.setTimeout(() => setQuery(search), 350);
    return () => { if (debounced.current) window.clearTimeout(debounced.current); };
  }, [search]);

  useEffect(() => {
    (async () => {
      try {
        const all = await api.get<Area[]>('/customers/routes');
        setAreas(all.filter((a) => a.status === 'active'));
      } catch {
        // The filter is a convenience; the list still works without it.
      }
    })();
  }, []);

  const load = useCallback(async (cursor?: string) => {
    if (cursor) setLoadingMore(true); else setLoading(true);
    try {
      const data = await api.get<CustomerList>('/customers', {
        search: query || undefined,
        routeId: routeId || undefined,
        status: status || undefined,
        cursor,
        limit: 50,
      });
      setColumns(data.columns);
      setRows((prev) => (cursor ? [...prev, ...data.customers] : data.customers));
      setNextCursor(data.nextCursor);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load customers.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [query, routeId, status, toast]);

  // Export every customer matching the current filter — not just the page
  // on screen — into one Excel sheet, using the same columns the table shows.
  const exportAll = useCallback(async () => {
    setExporting(true);
    try {
      const all: Record<string, string>[] = [];
      let expCols: CustomerListColumn[] = columns;
      let cursor: string | undefined;
      do {
        const data = await api.get<CustomerList>('/customers', {
          search: query || undefined,
          routeId: routeId || undefined,
          status: status || undefined,
          cursor,
          limit: 200,
        });
        if (data.columns.length) expCols = data.columns;
        for (const cust of data.customers) {
          const row: Record<string, string> = {};
          for (const c of expCols) row[c.key] = cellValue(cust, c);
          row.__status = cust.status;
          all.push(row);
        }
        cursor = data.nextCursor ?? undefined;
      } while (cursor);
      if (all.length === 0) { toast.error('No customers to export for this filter.'); return; }
      const cols: ReportColumn[] = [
        ...expCols.map((c) => ({ key: c.key, label: c.label, group: '', type: 'text' as const })),
        { key: '__status', label: 'Status', group: '', type: 'text' as const },
      ];
      await exportXlsx('customers', cols, all);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not export customers.');
    } finally {
      setExporting(false);
    }
  }, [columns, query, routeId, status, toast]);

  useEffect(() => { void load(); }, [load]);

  const filtered = Boolean(query || routeId || status);

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Every shop your reps have added, with the columns you chose on the form."
        actions={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => void exportAll()} disabled={exporting}>
              <Icon name="download" size={16} /> {exporting ? 'Exporting…' : 'Export'}
            </button>
            {canManage && (
              <>
                <button type="button" className="btn btn-ghost" onClick={() => setImporting(true)}>
                  <Icon name="download" size={16} /> Import
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                  <Icon name="plus" size={16} /> Add customer
                </button>
              </>
            )}
          </>
        )}
      />

      <div className="page">
        <div className="toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by name — spelling variations are matched"
          />

          <div style={{ width: 200 }}>
            <Select value={routeId} onChange={(e) => setRouteId(e.target.value)} aria-label="Area">
              <option value="">All areas</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </div>

          <div style={{ width: 170 }}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              <option value="">Any status</option>
              <option value="active">Active</option>
              <option value="pending_approval">Awaiting approval</option>
              <option value="inactive">Inactive</option>
              <option value="rejected">Rejected</option>
            </Select>
          </div>

          {filtered ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { setSearch(''); setRouteId(''); setStatus(''); }}
            >
              <Icon name="x" size={14} /> Clear
            </button>
          ) : null}
        </div>

        <div className="card">
          {loading ? (
            <div className="card-pad"><PageLoading /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="users"
              title={filtered ? 'No customers match those filters' : 'No customers yet'}
              body={filtered
                ? 'Try clearing a filter, or search for part of the shop name.'
                : 'Reps add customers from the app using the form you publish. Once they do, they show up here.'}
            />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      {columns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                      <th>Status</th>
                      <th>Added by</th>
                      <th>Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((customer) => (
                      <tr
                        key={customer.id}
                        className="clickable"
                        onClick={() => setOpenId(customer.id)}
                      >
                        {columns.map((column, i) => (
                          <td key={column.key} className={i === 0 ? 'primary' : undefined}>
                            {cellValue(customer, column)}
                          </td>
                        ))}
                        <td>
                          <Badge tone={STATUS_TONE[customer.status] ?? 'neutral'}>
                            {customer.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td>{customer.createdBy ?? '—'}</td>
                        <td className="nowrap">
                          {new Date(customer.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="between" style={{ padding: '14px 18px', borderTop: '1px solid var(--border)' }}>
                <span className="small faint">
                  {rows.length} customer{rows.length === 1 ? '' : 's'}
                  {nextCursor ? ' so far' : ''}
                </span>
                {nextCursor ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={loadingMore}
                    onClick={() => void load(nextCursor)}
                  >
                    {loadingMore ? <span className="spinner" /> : null}
                    Load more
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {openId ? (
        <CustomerDetail
          customerId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => { setOpenId(null); void load(); }}
        />
      ) : null}

      {creating ? (
        <CustomerCreateDialog
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); void load(); }}
        />
      ) : null}

      {importing ? (
        <CustomerImport
          onClose={() => setImporting(false)}
          onImported={() => { setImporting(false); void load(); }}
        />
      ) : null}
    </>
  );
}
